import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import {
  Individual,
  IndividualDeductionsReport,
  PayCalendar,
  Campaign,
  Group,
} from '@aslanpay/portal-common-lib';

import AuthLayout from 'app/components/Layout/AuthLayout';
import AuthenticatedLayout from 'app/components/Layout/AuthenticatedLayout';
import UnauthenticatedLayout from 'app/components/Layout/UnauthenticatedLayout';
import ReconciliationsSection from 'app/components/Reconciliations/ReconciliationsSection';

import { authPaths, companyPaths, globalPaths } from 'app/constants/routes';

import useAuthContext from 'app/hooks/useAuthContext';

import Admins from 'app/pages/Admins';
import Authentication from 'app/pages/Authentication';
import ChangePassword from 'app/pages/ChangePassword';
import Companies from 'app/pages/Companies';
import Dashboard from 'app/pages/Dashboard';
import Employees from 'app/pages/Employees';
import Gifting from 'app/pages/Gifting';
// import Home from 'app/pages/Home';
import ImportEmployees from 'app/pages/ImportEmployees';
import IndividualDeductionsReportProfile, {
  individualDeductionsReportProfileLoader,
} from 'app/pages/IndividualDeductionsReportProfile';
import CompanyDeductionsReportProfile from 'app/pages/CompanyDeductionsReportProfile';
import IndividualProfile, { individualProfileLoader } from 'app/pages/IndividualProfile';
import MyProfile from 'app/pages/MyProfile';
import DeductionReports from 'app/pages/DeductionReports';
import ReconciliationProfile from 'app/pages/ReconciliationProfile';
import Reconciliations from 'app/pages/Reconciliations';
import SalaryReconciliationProfile from 'app/pages/SalaryReconciliationProfile';
import PayCalendars from 'app/pages/PayCalendars';
import PayCalendarProfile, { payCalendarProfileLoader } from 'app/pages/PayCalendarProfile';
import SuspiciousActivity from 'app/pages/SuspiciousActivity';
import RewardEmployeesStepper from 'app/components/Gifting/BulkRewardEmployees';
import Error from 'app/pages/Error';

import { useManagingCompanyStore } from 'app/store';

import { getBlankPath } from 'app/utils/routes';
import Merchants from 'app/pages/Merchants';
import SystemSettings from 'app/pages/SystemSettings';
import IntegrationSyncHistory from 'app/pages/IntegrationSyncHistory';
import Partners from 'app/pages/Partners';
import UploadedFiles from 'app/pages/UploadedFiles';
import Approvals from 'app/pages/Approvals';
import Settings from 'app/pages/Settings';
import RecognitionsOverview from 'app/pages/RewardAndRecognition/Overview';
import Prides from 'app/pages/RewardAndRecognition/Prides';
import TheRoar from 'app/pages/RewardAndRecognition/TheRoar';
import RecognitionApprovals from 'app/pages/RewardAndRecognition/Approvals';
import RecognitionSettings from 'app/pages/RewardAndRecognition/Settings';
import Campaigns from 'app/pages/RewardAndRecognition/Campaigns';
import CampaignDetail, {
  campaignDetailLoader,
} from 'app/pages/RewardAndRecognition/Campaigns/CampaignDetail';
import CampaignPrideDetail, {
  campaignPrideDetailLoader,
} from 'app/pages/RewardAndRecognition/Campaigns/CampaignPrideDetail';
import WalletsAndPots from 'app/pages/WalletsAndPots';
import Pot from 'app/pages/Pot/Pot';

function Routes() {
  const queryClient = useQueryClient();

  const { user } = useAuthContext();

  // Routes for when we are managing all companies, all employees, all admins, etc
  const renderGlobalRoutes = () => (
    <Route element={<AuthenticatedLayout view="global" />}>
      <Route
        path={globalPaths.dashboard.path}
        handle={{ crumb: () => globalPaths.dashboard.crumb }}
      >
        <Route index element={<Dashboard />} />
      </Route>
      <Route path={globalPaths.suspiciousActivity.path} element={<SuspiciousActivity />} />
      <Route path={globalPaths.merchants.path} element={<Merchants />} />
      <Route path={globalPaths.partners.path} element={<Partners />} />
      <Route path={globalPaths.systemSettings.path} element={<SystemSettings />} />
      <Route path={globalPaths.syncHistory.path} element={<IntegrationSyncHistory />} />
      <Route path={globalPaths.profile.path} element={<MyProfile />} />
      <Route path={globalPaths.changePassword.path} element={<ChangePassword />} />
      <Route path={globalPaths.companies.path} element={<Companies />} />
      <Route path={globalPaths.approvals.path} element={<Approvals />} />
      <Route
        path={globalPaths.employees.path}
        handle={{ crumb: () => globalPaths.employees.crumb }}
      >
        <Route index element={<Employees />} />
        <Route
          path={globalPaths.employeeProfile.path}
          element={<IndividualProfile />}
          loader={individualProfileLoader(queryClient)}
          handle={{
            crumb: (data: Individual) =>
              data.firstName ? `${data.firstName} ${data.lastName}` : data.email,
          }}
        />
        <Route
          path={globalPaths.importEmployees.path}
          element={<ImportEmployees />}
          handle={{ crumb: () => globalPaths.importEmployees.crumb }}
        />
      </Route>

      <Route path={globalPaths.payrollReports.path} element={<DeductionReports />} />
      <Route
        path={globalPaths.reconciliations.path}
        element={<ReconciliationsSection />}
        handle={{ crumb: () => globalPaths.reconciliations.crumb }}
      >
        <Route index element={<Reconciliations />} />
        <Route
          path={globalPaths.reconciliationProfile.path}
          element={<ReconciliationProfile />}
          handle={{ crumb: () => globalPaths.reconciliationProfile.crumb }}
        />
        <Route
          path={globalPaths.salaryReconciliationProfile.path}
          element={<SalaryReconciliationProfile />}
          handle={{ crumb: () => globalPaths.salaryReconciliationProfile.crumb }}
        />
      </Route>
      <Route
        path={globalPaths.companyDeductionsReportProfile.path}
        element={<CompanyDeductionsReportProfile />}
        handle={{ crumb: () => globalPaths.companyDeductionsReportProfile.crumb }}
      />
      <Route
        path={globalPaths.individualDeductionsReportProfile.path}
        element={<IndividualDeductionsReportProfile />}
        loader={individualDeductionsReportProfileLoader(queryClient)}
        handle={{
          crumb: (
            data: IndividualDeductionsReport & { payCycleName: string; employeeName: string }
          ) => `${data.employeeName} - ${data.payCycleName}`,
        }}
      />
      <Route path={globalPaths.admins.path} handle={{ crumb: () => globalPaths.admins.crumb }}>
        <Route index element={<Admins />} />
        <Route
          path={globalPaths.adminProfile.path}
          element={<IndividualProfile />}
          loader={individualProfileLoader(queryClient)}
          handle={{
            crumb: (data: Individual) =>
              data.firstName ? `${data.firstName} ${data.lastName}` : data.email,
          }}
        />
      </Route>
    </Route>
  );

  // Routes for when we are managing a specific company
  const renderCompanyRoutes = () => (
    <Route element={<AuthenticatedLayout view="company" />}>
      <Route
        path={globalPaths.companies.path}
        handle={{ crumb: () => globalPaths.companies.crumb }}
      >
        <Route
          path={companyPaths.home.path}
          handle={{ crumb: () => useManagingCompanyStore.getState().company?.name }}
        >
          {/* TODO: Temporary redirect to blank path while we don't decide if we remove the home/notifications/analytics/merchants pages */}
          <Route index element={<Navigate to={getBlankPath()} replace />} />

          {/* TODO: Home/Notifications/Analytics/Merchants pages commented until we decide if we want to permanently remove them */}
          {/* <Route index element={<Home />} />
          <Route
            path={companyPaths.notifications.path}
            element={<Notifications />}
            handle={{ crumb: () => companyPaths.notifications.crumb }}
          />
          <Route
            path={companyPaths.analytics.path}
            handle={{ crumb: () => companyPaths.analytics.crumb }}
          >
            <Route index element={<Analytics />} />
          </Route> */}

          <Route
            path={companyPaths.dashboard.path}
            handle={{ crumb: () => companyPaths.dashboard.crumb }}
          >
            <Route index element={<Dashboard />} />
          </Route>

          <Route
            path={companyPaths.employees.path}
            handle={{ crumb: () => companyPaths.employees.crumb }}
          >
            <Route index element={<Employees />} />
            <Route
              path={companyPaths.employeeProfile.path}
              element={<IndividualProfile />}
              loader={individualProfileLoader(queryClient)}
              handle={{
                crumb: (data: Individual) =>
                  data.firstName ? `${data.firstName} ${data.lastName}` : data.email,
              }}
            />
            <Route
              path={companyPaths.importEmployees.path}
              element={<ImportEmployees />}
              handle={{ crumb: () => companyPaths.importEmployees.crumb }}
            />
          </Route>

          <Route
            path={companyPaths.payrollReports.path}
            element={<DeductionReports />}
            handle={{ crumb: () => companyPaths.payrollReports.crumb }}
          />
          <Route
            path={companyPaths.reconciliations.path}
            element={<ReconciliationsSection />}
            handle={{ crumb: () => companyPaths.reconciliations.crumb }}
          >
            <Route index element={<Reconciliations />} />
            <Route
              path={companyPaths.reconciliationProfile.path}
              element={<ReconciliationProfile />}
              handle={{ crumb: () => companyPaths.reconciliationProfile.crumb }}
            />
            <Route
              path={companyPaths.salaryReconciliationProfile.path}
              element={<SalaryReconciliationProfile />}
              handle={{ crumb: () => companyPaths.salaryReconciliationProfile.crumb }}
            />
          </Route>
          <Route
            path={companyPaths.companyDeductionsReportProfile.path}
            element={<CompanyDeductionsReportProfile />}
            handle={{ crumb: () => companyPaths.companyDeductionsReportProfile.crumb }}
          />
          <Route
            path={companyPaths.individualDeductionsReportProfile.path}
            element={<IndividualDeductionsReportProfile />}
            loader={individualDeductionsReportProfileLoader(queryClient)}
            handle={{
              crumb: (
                data: IndividualDeductionsReport & { payCycleName: string; employeeName: string }
              ) => `${data.employeeName} - ${data.payCycleName}`,
            }}
          />
          <Route
            path={companyPaths.payCalendars.path}
            handle={{ crumb: () => companyPaths.payCalendars.crumb }}
          >
            <Route index element={<PayCalendars />} />
            <Route
              path={companyPaths.payCalendarProfile.path}
              element={<PayCalendarProfile />}
              loader={payCalendarProfileLoader(queryClient)}
              handle={{ crumb: (data: PayCalendar) => data.name }}
            />
          </Route>

          <Route
            path={companyPaths.gifting.path}
            element={<Gifting />}
            handle={{ crumb: () => companyPaths.gifting.crumb }}
          />

          <Route
            path={companyPaths.recognitions.path}
            handle={{ crumb: () => companyPaths.recognitions.crumb }}
          >
            <Route index element={<RecognitionsOverview />} />
            <Route
              path={companyPaths.prides.path}
              element={<Prides />}
              handle={{ crumb: () => companyPaths.prides.crumb }}
            />
            <Route
              path={companyPaths.theRoar.path}
              element={<TheRoar />}
              handle={{ crumb: () => companyPaths.theRoar.crumb }}
            />
            <Route
              path={companyPaths.recognitionApprovals.path}
              element={<RecognitionApprovals />}
              handle={{ crumb: () => companyPaths.recognitionApprovals.crumb }}
            />
            <Route
              path={companyPaths.campaigns.path}
              handle={{ crumb: () => companyPaths.campaigns.crumb }}
            >
              <Route index element={<Campaigns />} />
              <Route
                path={companyPaths.campaignDetail.path}
                loader={campaignDetailLoader(queryClient)}
                handle={{
                  crumb: (data: Campaign | null) => data?.name ?? companyPaths.campaignDetail.crumb,
                }}
              >
                <Route index element={<CampaignDetail />} />
                <Route
                  path={companyPaths.campaignPrideDetail.path}
                  element={<CampaignPrideDetail />}
                  loader={campaignPrideDetailLoader(queryClient)}
                  handle={{
                    crumb: (data: Group | null) =>
                      data?.name ?? companyPaths.campaignPrideDetail.crumb,
                  }}
                />
              </Route>
            </Route>
            <Route
              path={companyPaths.recognitionSettings.path}
              element={<RecognitionSettings />}
              handle={{ crumb: () => companyPaths.recognitionSettings.crumb }}
            />
          </Route>

          <Route
            path={companyPaths.walletsAndPots.path}
            handle={{ crumb: () => companyPaths.walletsAndPots.crumb }}
          >
            <Route index element={<WalletsAndPots />} />
            <Route
              path={companyPaths.pot.path}
              element={<Pot />}
              handle={{ crumb: () => companyPaths.pot.crumb }}
            />
          </Route>

          <Route
            path={companyPaths.uploadedFiles.path}
            element={<UploadedFiles />}
            handle={{ crumb: () => companyPaths.uploadedFiles.crumb }}
          />

          <Route
            path={companyPaths.settings.path}
            element={<Settings />}
            handle={{ crumb: () => companyPaths.settings.crumb }}
          />

          <Route path="reward-employees" element={<RewardEmployeesStepper />} />

          <Route
            path={companyPaths.admins.path}
            handle={{ crumb: () => companyPaths.admins.crumb }}
          >
            <Route index element={<Admins />} />
            <Route
              path={companyPaths.adminProfile.path}
              element={<IndividualProfile />}
              loader={individualProfileLoader(queryClient)}
              handle={{
                crumb: (data: Individual) =>
                  data.firstName ? `${data.firstName} ${data.lastName}` : data.email,
              }}
            />
          </Route>
        </Route>
      </Route>
    </Route>
  );

  const renderUnauthenticatedRoutes = () => (
    <Route element={<UnauthenticatedLayout />}>
      <Route path={authPaths.authentication} element={<Authentication />} />
    </Route>
  );

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<AuthLayout />} errorElement={<Error />}>
        <Route
          path="/"
          element={
            user ? (
              <Navigate to={getBlankPath()} replace />
            ) : (
              <Navigate to={authPaths.authentication} replace />
            )
          }
        />
        {renderGlobalRoutes()}
        {renderCompanyRoutes()}
        {renderUnauthenticatedRoutes()}
      </Route>
    )
  );

  return <RouterProvider router={router} />;
}

export default Routes;
